'use client';

import { useCallback, useState, useEffect } from 'react';
import Link from 'next/link';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../../components/Footer';
import HorizontalFilterBar from '../../../../components/HorizontalFilterBar';
import AdBanner from '../../../../components/AdBanner';
import FilterPanel from '../../../../components/FilterPanel';
import { api } from '@/lib/apiClient';
import { useSearchParams } from 'next/navigation';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

// Mock Plots Data
const mockPlots = [
    {
        id: 'plot-001',
        title: 'Premium Villa Plot in OMR',
        price: 45,
        priceUnit: 'Lac',
        area: 1200,
        areaUnit: 'sqft',
        pricePerSqft: 3750,
        locality: 'Thiruporur',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600',
        type: 'Residential',
        approval: 'DTCP',
        dimensions: '30x40',
        facing: 'North',
        boundary: 'Fenced',
        isNew: true,
        isVerified: true,
        isFeatured: true,
    },
    {
        id: 'plot-002',
        title: 'Corner Plot near Tambaram',
        price: 85,
        priceUnit: 'Lac',
        area: 2400,
        areaUnit: 'sqft',
        pricePerSqft: 3540,
        locality: 'Tambaram West',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1623298317883-6b70254edf31?w=600',
        type: 'Residential',
        approval: 'CMDA',
        dimensions: '40x60',
        facing: 'East',
        boundary: 'Wall',
        isNew: false,
        isVerified: true,
        isFeatured: false,
    },
    {
        id: 'plot-003',
        title: 'Commercial Land on Main Road',
        price: 2.5,
        priceUnit: 'Cr',
        area: 4800,
        areaUnit: 'sqft',
        pricePerSqft: 5200,
        locality: 'Medavakkam',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1599809275671-b5942cabc7a2?w=600',
        type: 'Commercial',
        approval: 'CMDA',
        dimensions: '60x80',
        facing: 'South',
        boundary: 'Fenced',
        isNew: true,
        isVerified: true,
        isFeatured: true,
    },
    {
        id: 'plot-004',
        title: 'Budget Plot in Oragadam',
        price: 18,
        priceUnit: 'Lac',
        area: 1500,
        areaUnit: 'sqft',
        pricePerSqft: 1200,
        locality: 'Oragadam',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1623298317883-6b70254edf31?w=600',
        type: 'Industrial',
        approval: 'DTCP',
        dimensions: '30x50',
        facing: 'West',
        boundary: 'Open',
        isNew: false,
        isVerified: false,
        isFeatured: false,
    },
    {
        id: 'plot-005',
        title: 'Farm Land with Coconut Trees',
        price: 60,
        priceUnit: 'Lac',
        area: 10000,
        areaUnit: 'sqft',
        pricePerSqft: 600,
        locality: 'Guduvanchery',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1500076656116-558758c991c1?w=600',
        type: 'Farm Land',
        approval: 'Panchayat',
        dimensions: '100x100',
        facing: 'North',
        boundary: 'Hedge',
        isNew: true,
        isVerified: true,
        isFeatured: false,
    },
    {
        id: 'plot-006',
        title: 'Gated Community Plot',
        price: 32,
        priceUnit: 'Lac',
        area: 1000,
        areaUnit: 'sqft',
        pricePerSqft: 3200,
        locality: 'Vandalur',
        city: 'Chennai',
        image: 'https://images.unsplash.com/photo-1592595896551-12b371d546d5?w=600',
        type: 'Residential',
        approval: 'RERA',
        dimensions: '25x40',
        facing: 'East',
        boundary: 'Wall',
        isNew: true,
        isVerified: true,
        isFeatured: true,
    },
];

// Mock Ad Data
const adBanners = [
    {
        id: 'ad-plot-1',
        title: 'Plots from ₹9.99 Lacs',
        description: 'DTCP approved plots in fast developing corridor. High appreciation guaranteed.',
        image: 'https://images.unsplash.com/photo-1500076656116-558758c991c1?w=600',
        ctaText: 'Download Brochure'
    },
    {
        id: 'ad-plot-2',
        title: 'Managed Farm Lands',
        description: 'Own a farm land near Chennai. We manage the plantation for you.',
        image: 'https://images.unsplash.com/photo-1500076656116-558758c991c1?w=600',
        ctaText: 'Visit Site'
    }
];

const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
];

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

export default function PlotsListingPage() {
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState('relevance');
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [mockPlots, setMockPlots] = useState([]);
    const searchParams = useSearchParams();
    const cityId = searchParams.get("cityId");
    const [city_name, setCityName] = useState("");
    const [localities, setLocalities] = useState("");
    const [parms, setParms] = useState("");

    // Read localities from query params if any are present
    const localitiesParam = searchParams.get("localities");
    const initialLocalities = localitiesParam ? localitiesParam.split('-').map(num => parseInt(num, 10)) : [];

    const [localitiesIds, setLocalitiesIds] = useState(initialLocalities);

    useEffect(() => {
        fetchProperties();
    }, [searchParams]);

    const fetchProperties = async () => {
        try {
            setParms(searchParams.toString());
            const localitiesArray = convertToArray(searchParams.get("localities"));
            setLocalitiesIds(localitiesArray);
            let whereClause = "property_type=6 and listing_type=$1 and city_id=$2";
            let params = ['sell', cityId];

            const res = await api.post('selectwithout/view_home_cities', { sqlData: "*", where: "id=$1", whereData: [cityId] });
            if (res.data.status) {
                setCityName(res.data.data[0].city_name);
            }

            if (localitiesArray && localitiesArray.length > 0) {
                whereClause += " and locality_id = ANY($3)";
                params.push(localitiesArray);
            }

            const response = await api.post('selectwithout/view_home_listings',
                {
                    sqlData: "*",
                    where: whereClause,
                    whereData: params,
                });
            console.log("response.data", response.data);
            let property_card = [];
            for (let i = 0; i < response.data.length; i++) {
                property_card.push(response.data[i].property_card);
            }
            setMockPlots(property_card);
        } catch (error) {
            console.error('Error fetching properties:', error);
        }
    };

    // Filter logic (mock)
    const filteredPlots = mockPlots.filter(plot => {
        // Implement actual filtering logic here based on 'filters' object
        return true;
    });

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        console.log('Plots Filters changed:', newFilters);
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen bg-gray">
                {/* Horizontal Filter Bar - Sticky */}
                <div className="hidden lg:block sticky top-16 z-40 shadow-lg shadow-primary/10">
                    <HorizontalFilterBar
                        filters={filters}
                        onChange={handleFilterChange}
                        listingType="plots"
                    />
                </div>

                {/* Page Header */}
                <div className="border-b border-gray-100">
                    <div className="container-custom py-2">
                        <div className="flex items-center justify-between mt-5 min-h-[40px]">
                            <nav className="flex items-center gap-2 text-sm text-gray-500" aria-label="Breadcrumb">
                                <Link href="/" className="hover:text-primary">Home</Link>
                                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-gray-900 font-medium">
                                    <strong> Available {filteredPlots.length}+ Properties </strong>  for Sale in {localities} {city_name}
                                </span>
                            </nav>
                            {/* RIGHT – Relevance Select */}
                            <div className="flex items-center gap-2">
                                <label htmlFor="sortBy" className="text-sm text-gray-600">Sort by:</label>
                                <select
                                    id="sortBy"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="h-8 text-sm px-2 pr-6 rounded-md bg-white border border-gray-300 text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                >
                                    {sortOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Split Layout */}
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-4 items-start">

                        {/* Left: Plots List (8 cols) */}
                        <div className="lg:col-span-8 flex flex-col gap-6">

                            {/* Mobile Filter Button */}
                            <div className="lg:hidden">
                                <button
                                    onClick={() => setIsMobileFilterOpen(true)}
                                    className="btn btn-secondary w-full flex justify-between items-center"
                                >
                                    <span className="flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                        </svg>
                                        Filters
                                    </span>
                                    {Object.keys(filters).length > 0 && (
                                        <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                            {Object.keys(filters).length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Plots List Cards */}
                            {filteredPlots.map((plot) => (
                                <Link
                                    key={plot.id}
                                    href={`/plots/p/${plot.id}?${parms}`}
                                    className="group relative flex flex-col md:flex-row bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                >
                                    {/* Image Section */}
                                    <div className="relative md:w-64 md:shrink-0 h-56 md:h-auto overflow-hidden">
                                        <img
                                            src={plot.image ? (plot.image.startsWith('http') ? plot.image : `${BASE_URL}${plot.image}`) : '/no-image.jpeg'}
                                            alt={plot.title}
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 md:opacity-30" />

                                        {/* Top Badges */}
                                        <div className="absolute top-3 left-3 flex flex-col gap-2">
                                            {plot.isFeatured && (
                                                <span className="px-2 py-1 text-xs font-bold uppercase bg-yellow-400 text-yellow-900 rounded shadow-sm w-fit">
                                                    Featured
                                                </span>
                                            )}
                                            {plot.approval && (
                                                <span className="px-2 py-1 text-xs font-bold uppercase bg-white/90 text-primary rounded shadow-sm w-fit backdrop-blur-md">
                                                    {plot.approval}
                                                </span>
                                            )}
                                        </div>

                                        {/* Bottom Badge (Plot Type) */}
                                        <div className="absolute bottom-3 left-3">
                                            <span className="px-2.5 py-1 text-xs font-semibold bg-black/60 backdrop-blur-sm text-white rounded-full shadow-sm">
                                                {plot.type}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Content Section */}
                                    <div className="flex-1 p-5 lg:p-6 flex flex-col">

                                        {/* Header Row */}
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary transition-colors line-clamp-1">
                                                    {plot.title}
                                                </h3>
                                                <p className="flex items-center gap-1 text-slate-500 text-sm mt-1">
                                                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    {plot.locality}, {plot.city}
                                                </p>
                                            </div>
                                            <div className="text-right shrink-0 ml-4">
                                                <div className="text-2xl font-bold text-gray-900">
                                                    ₹{plot.price} <span className="text-lg font-semibold">{plot.priceUnit}</span>
                                                </div>
                                                <div className="text-xs text-slate-500 font-medium whitespace-nowrap">
                                                    Avg. ₹{plot.pricePerSqft}/sq.ft
                                                </div>
                                            </div>
                                        </div>

                                        {/* Key Details Grid */}
                                        <div className="bg-slate-50 rounded-xl p-4 mt-2 mb-4 grid grid-cols-3 gap-2 border border-slate-100">
                                            <div className="flex flex-col items-center justify-center p-1 border-r border-slate-200 last:border-0">
                                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Plot Area</span>
                                                <span className="text-base font-bold text-primary">{plot.area}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">{plot.areaUnit}</span>
                                            </div>
                                            <div className="flex flex-col items-center justify-center p-1 border-r border-slate-200 last:border-0">
                                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Dimensions</span>
                                                <span className="text-base font-bold text-slate-800">{plot.dimensions}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">Feet</span>
                                            </div>
                                            <div className="flex flex-col items-center justify-center p-1">
                                                <span className="text-xs text-slate-500 uppercase font-semibold mb-0.5">Facing</span>
                                                <span className="text-base font-bold text-slate-800">{plot.facing}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">{plot.boundary}</span>
                                            </div>
                                        </div>

                                        {/* CTA Row */}
                                        <div className="mt-auto flex items-center justify-between pt-2">
                                            <div className="flex gap-2">
                                                {plot.isNew && <span className="text-[10px] font-bold tracking-wider uppercase text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">New Arrival</span>}
                                            </div>
                                            <button className="px-4 py-2 bg-slate-900 hover:bg-primary text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-slate-200">
                                                View Details
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </Link>
                            ))}

                            {/* Load More */}
                            <div className="mt-8 text-center">
                                <button className="btn btn-white border border-gray-200 hover:border-primary hover:text-primary px-8 w-full sm:w-auto">
                                    Load More Plots
                                </button>
                            </div>
                        </div>

                        {/* Right: Ad Sidebar (4 cols) */}
                        <aside className="lg:col-span-4 hidden lg:flex flex-col gap-6 sticky top-24">
                            {/* Featured Widget */}
                            {/* <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl border border-emerald-100">
                                <h3 className="text-lg font-bold text-emerald-900 mb-2">Plot Buying Guide</h3>
                                <p className="text-sm text-emerald-700 mb-4">
                                    Confused about DTCP vs CMDA? Download our free guide on how to safely buy land in Chennai.
                                </p>
                                <button className="w-full btn bg-emerald-600 text-white hover:bg-emerald-700 border-none shadow-lg shadow-emerald-200">
                                    Download Guide
                                </button>
                            </div> */}

                            {/* Ad Banners */}
                            {adBanners.map((ad) => (
                                <AdBanner
                                    key={ad.id}
                                    {...ad}
                                />
                            ))}
                        </aside>

                    </div>
                </div>

                {/* Mobile Filter Modal */}
                {isMobileFilterOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div
                            className="absolute inset-0 bg-black/50"
                            onClick={() => setIsMobileFilterOpen(false)}
                        />
                        <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white overflow-y-auto">
                            <FilterPanel
                                filters={filters}
                                onChange={handleFilterChange}
                                listingType="plots" // Pass plots type
                                isMobile={true}
                                onClose={() => setIsMobileFilterOpen(false)}
                            />
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    );
}
